import { type BracketMatch, type BracketRound, type GameParticipantId } from '../../model'
import { isEmpty, isNotEmpty } from '../isEmpty'

type Matchup = {
  id: number
  player1: number
  player2: number
  fav: number
  order?: number
  winnerGame?: number
}

export type WithGameParticipantId = { participantId: GameParticipantId }

export function generateBracket(numOfPlayers: number): Map<number, Matchup[]> {
  const totalRounds = Math.ceil(Math.log2(numOfPlayers))
  const roundMap: Map<number, Matchup[]> = new Map()
  const paddedPlayers = nextPowerOfTwo(numOfPlayers) - 1

  let matchId = paddedPlayers

  for (let round = 1; round <= totalRounds; round++) {
    const matchesInRound: Matchup[] = []
    const players = Array.from({ length: 2 ** (round - 1) }, (_, index) => index + 1)
    const currRound = totalRounds - round + 1

    for (let match = 0; match < players.length; match++) {
      const player1 = players[players.length - match - 1]
      const currentPlayer = players.length + 1 + match
      const player2 = currentPlayer <= numOfPlayers ? currentPlayer : -1

      if (matchId === 0) break

      matchesInRound.push({
        id: matchId--,
        player1,
        player2,
        fav: player1,
        winnerGame: getWinnerGame(roundMap, currRound + 1, player1),
      })
    }

    roundMap.set(currRound, matchesInRound)
  }

  addOrder(roundMap, paddedPlayers)
  return roundMap
}

function addOrder(roundMap: Map<number, Matchup[]>, totalPlayers: number): void {
  const idMap = flattenMatchups(roundMap)
  const matchTree = buildMatchTree(totalPlayers, idMap)
  if (!matchTree) return

  const queue: MatchNode[] = [matchTree]
  let order = totalPlayers

  while (isNotEmpty(queue)) {
    const length = queue.length
    for (let i = 0; i < length; i++) {
      const current = queue.shift()
      if (!current) continue
      current.match.order = order--
      if (current.right) queue.push(current.right)
      if (current.left) queue.push(current.left)
      idMap.set(current.match.id, current.match)
    }
  }

  roundMap.forEach((matches) => {
    matches.forEach((match) => {
      match = idMap.get(match.id) ?? match
    })
    matches.sort((a, b) => a.order! - b.order!)
  })
}

function flattenMatchups(roundMap: Map<number, Matchup[]>): Map<number, Matchup> {
  const idMap = new Map<number, Matchup>()
  for (const matches of roundMap.values()) {
    for (const match of matches) {
      idMap.set(match.id, match)
    }
  }
  return idMap
}

type MatchNode = { match: Matchup; left?: MatchNode; right?: MatchNode }
function buildMatchTree(matchId: number, idMap: Map<number, Matchup>, direction = 1): MatchNode | undefined {
  const match = idMap.get(matchId)
  if (!match) return

  const winnerGames = [...idMap.values()].filter((m) => m.winnerGame === matchId).sort((a, b) => a.fav - b.fav)
  const left = winnerGames.at(0)
  const right = winnerGames.at(1)

  if (direction === -1) {
    return {
      match,
      left: right ? buildMatchTree(right.id, idMap, 1) : undefined,
      right: left ? buildMatchTree(left.id, idMap, -1) : undefined,
    }
  }

  return {
    match,
    left: left ? buildMatchTree(left.id, idMap, 1) : undefined,
    right: right ? buildMatchTree(right.id, idMap, -1) : undefined,
  }
}

function getWinnerGame(map: Map<number, Matchup[]>, round: number, player: number): number | undefined {
  const matches = map.get(round)
  const match = matches?.find((m) => m.player1 === player || m.player2 === player)
  return match?.id
}

export function fillRoundsWithMatches(rounds: BracketRound[], participantIds: WithGameParticipantId[]): BracketRound[] {
  const roundMap = generateBracket(participantIds.length)
  const uniquePlayers = new Set<number>()
  rounds.sort((a, b) => a.round - b.round)
  return rounds.map((round): BracketRound => {
    const matches = roundMap.get(round.round)
    if (isEmpty(matches)) return round

    return {
      ...round,
      matches: matches.map((match): BracketMatch => {
        const bye = match.player2 < 0
        const player1 = uniquePlayers.has(match.player1) ? undefined : participantIds[match.player1 - 1]
        const player2 = uniquePlayers.has(match.player2) && !bye ? undefined : participantIds[match.player2 - 1]
        if (!bye) {
          uniquePlayers.add(match.player1)
          uniquePlayers.add(match.player2)
        }

        return {
          id: match.id,
          participant1: player1?.participantId,
          participant2: player2?.participantId,
          winnerGame: match.winnerGame,
          order: match.order,
        }
      }),
    }
  })
}

export function nextPowerOfTwo(n: number): number {
  return 2 ** Math.ceil(Math.log2(n))
}

export function getNumOfPlayers(participantLength: number, bracketSize?: number): number {
  if (isEmpty(bracketSize)) return participantLength
  if (participantLength >= bracketSize) return bracketSize
  return participantLength
}
